/**
 * OsInventoryCountSheet — OS-INV-16 / OS-INV-18 / OS-INV-21
 *
 * OS-specific count sheet generator with location picker and mode selector.
 * Replaces the shared InventoryCountSheet for /os/count-sheet.
 *
 * ── Modes (OS-INV-21) ────────────────────────────────────────────────────
 *   • "All Items" (default, unchanged) — generates a session with every
 *     material in the catalog for the chosen location. Existing behaviour.
 *   • "Monthly Process" — generates a session from the weekly due list
 *     (GET /api/os/audit/schedule/week, sourced from OS-INV-20 logic).
 *     Requires an active inventory cycle. If no active cycle, the generate
 *     button is disabled and a link to /os/audit is shown.
 *
 * ── Flows ────────────────────────────────────────────────────────────────
 *   All Items:
 *     1. SETUP   → pick mode + location
 *     2. LOADING → POST /count-sessions {location} + QR + materials
 *     3. READY   → printable table (auto-print)
 *
 *   Monthly Process:
 *     1. SETUP   → mode selected → cycle check runs immediately
 *                → pre-gen summary shown (week #, item count, overdue)
 *     2. LOADING → POST /count-sessions {mode:"monthly_process", cycle_id, location}
 *     3. MONTHLY-DONE → post-gen summary + direct link to count sheet
 *
 * ── API (OS-INV-21 additions) ────────────────────────────────────────────
 *   GET  /api/os/audit/cycles/active      → active cycle or 404
 *   GET  /api/os/audit/schedule/week      → { items, week, cap, total_remaining_this_cycle }
 *   POST /api/os/count-sessions           → extended with { mode, cycle_id }
 *
 * ── OS-INV-18 (Blind Counting) ───────────────────────────────────────────
 *   "System Count" column removed from the printable table. systemCount is
 *   still computed in buildRows for internal use; it is simply not rendered.
 *
 * ── Employee UX ──────────────────────────────────────────────────────────
 *   No change. Generated sessions are identical in structure. Employees
 *   count items using the same scan/entry flow with no scheduling labels.
 */

import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import PrintIcon from '@mui/icons-material/Print';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import QRCode from 'qrcode';
import { authFetch } from '../../madebb/utils/authFetch';
import { useTenant } from '../../contexts/TenantContext';
import { buildCopies } from '../utils/buildCopies';

// ── Types ──────────────────────────────────────────────────────────────────

export type CountLocation = 'all' | 'storeroom' | 'working';

/** OS-INV-21: count sheet generation mode. */
type GeneratorMode = 'all' | 'monthly_process';

interface LocationMeta {
  label: string;
  color: 'default' | 'primary' | 'success';
}

const LOCATION_META: Record<CountLocation, LocationMeta> = {
  all:       { label: 'All Locations',    color: 'default'  },
  storeroom: { label: 'Store Room',        color: 'success'  },
  working:   { label: 'Working Inventory', color: 'primary'  },
};

interface CountSession {
  sessionId: string;
  createdAt: string;
  expiresAt: string;
  location?: CountLocation;
}

export interface OsMaterial {
  id: string;
  name: string;
  type: string;
  unit: string;
  currentStock: number;
  /** snake_case — not camelCased by formatMaterialFromDB (added in OS-INV-11). */
  stock_storeroom: number;
  /** snake_case — not camelCased by formatMaterialFromDB (added in OS-INV-11). */
  stock_working: number;
  itemNumber?: number;
  /** OS-INV-17: system-assigned per-location item numbers. */
  locationItemNumbers?: { storeroom: number | null; working: number | null };
  isMaster?: boolean;
}

/**
 * OS-INV-17: a single printable row = a material AT a specific location, keyed by
 * that location's item number. A material stocked in both locations yields two
 * rows. This is what makes the count sheet's Item # match the labels exactly.
 */
export interface CountSheetRow {
  key: string;
  itemNumber: number;
  materialId: string;
  name: string;
  type: string;
  unit: string;
  location: 'storeroom' | 'working';
  /** OS-INV-18: computed but not rendered (blind count). */
  systemCount: number;
}

/** OS-INV-21: active inventory cycle from GET /api/os/audit/cycles/active. */
interface ActiveCycle {
  id: number;
  name: string;
  status: string;
  started_at: string;
  progress: {
    total_due: number;
    counted_this_cycle: number;
    percent_complete: number;
    overdue: number;
    weeks_elapsed: number;
  };
}

/** OS-INV-21: weekly due list from GET /api/os/audit/schedule/week. */
interface WeekSchedule {
  items: Array<{ is_overdue?: boolean; urgency?: string; [key: string]: unknown }>;
  week: number;
  cap: number;
  total_remaining_this_cycle: number;
}

/** Shape of items returned by GET /api/os/count-sessions/:id/items (with location + unit). */
interface SessionItem {
  itemNumber: number;
  materialId: string;
  materialName: string;
  location: 'storeroom' | 'working' | null;
  unit: string | null;
  systemCount: number;
  actualCount: number;
  difference: number;
  timestamp: string | null;
}

/** OS-INV-21: response body from POST /api/os/count-sessions with mode="monthly_process". */
interface MonthlySessionResult {
  session_id: string;
  mode: string;
  cycle_id: number;
  week: number;
  item_count: number;
  overdue_included: number;
  class_breakdown: { A: number; B: number; C: number };
  /** Absolute path, e.g. /os/count/abc-123. Prepend window.location.origin for full URL. */
  count_url: string;
}

type Phase = 'setup' | 'loading' | 'ready' | 'monthly-done' | 'error';

/** OS-INV-21: state of the active cycle check triggered when Monthly Process is selected. */
type CycleCheckPhase = 'idle' | 'checking' | 'active' | 'none' | 'check-error';

// ── Helpers ────────────────────────────────────────────────────────────────

const ROW_LOCATION_LABEL: Record<'storeroom' | 'working', string> = {
  storeroom: 'Store Room',
  working:   'Working',
};

/**
 * OS-INV-17: build count-sheet rows from per-location item numbers.
 * For each material, emit one row per location that (a) is in scope for the
 * session and (b) has a system-assigned item number. Each row shows that
 * location's own stock bucket as the System Count — stored but not rendered
 * per OS-INV-18 (blind count).
 */
export function buildRows(materials: OsMaterial[], loc: CountLocation): CountSheetRow[] {
  const wantStoreroom = loc === 'all' || loc === 'storeroom';
  const wantWorking   = loc === 'all' || loc === 'working';
  const rows: CountSheetRow[] = [];
  for (const m of materials) {
    if (m.isMaster) continue;
    const nums = m.locationItemNumbers;
    if (!nums) continue;
    if (wantStoreroom && nums.storeroom != null) {
      rows.push({
        key:        `${m.id}::storeroom`,
        itemNumber: nums.storeroom,
        materialId: m.id,
        name:       m.name,
        type:       m.type,
        unit:       m.unit,
        location:   'storeroom',
        systemCount: m.stock_storeroom ?? 0,
      });
    }
    if (wantWorking && nums.working != null) {
      rows.push({
        key:        `${m.id}::working`,
        itemNumber: nums.working,
        materialId: m.id,
        name:       m.name,
        type:       m.type,
        unit:       m.unit,
        location:   'working',
        systemCount: m.stock_working ?? 0,
      });
    }
  }
  // Sort by item number so the printed sheet order matches the labels.
  rows.sort((a, b) => a.itemNumber - b.itemNumber);
  return rows;
}

// ── Audit class colour map (for post-gen summary chips) ────────────────────

const CLASS_CHIP_SX = {
  A: { bgcolor: '#c6282818', color: '#c62828', border: '1px solid #c6282840' },
  B: { bgcolor: '#e6510018', color: '#e65100', border: '1px solid #e6510040' },
  C: { bgcolor: '#1565c018', color: '#1565c0', border: '1px solid #1565c040' },
} as const;

// ── OsInventoryCountSheet ──────────────────────────────────────────────────

const OsInventoryCountSheet: React.FC = () => {
  const { apiBase, routeBase } = useTenant();

  // ── General phase state ──────────────────────────────────────────────
  const [phase, setPhase]       = useState<Phase>('setup');
  const [errorMsg, setErrorMsg] = useState<string>('');

  // ── Mode selector — OS-INV-21 ────────────────────────────────────────
  const [mode, setMode]         = useState<GeneratorMode>('monthly_process');

  // ── Location picker (both modes) ─────────────────────────────────────
  const [location, setLocation] = useState<CountLocation>('all');

  // ── All-Items state (existing) ───────────────────────────────────────
  const [session, setSession]     = useState<CountSession | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  // #1: per-item QR codes keyed by 'itemNumber:location'
  const [itemQRs, setItemQRs] = useState<Record<string, string>>({});
  // #4: multi-copy print — how many copies to print
  const [nCopies, setNCopies] = useState(1);
  // MADE-299: extra items filter state
  const [extraPanelOpen, setExtraPanelOpen]         = useState(false);
  const [extraVendor, setExtraVendor]               = useState('');
  const [extraType, setExtraType]                   = useState('');
  const [extraQtyMin, setExtraQtyMin]               = useState('');
  const [extraQtyMax, setExtraQtyMax]               = useState('');
  const [extraItemSearch, setExtraItemSearch]       = useState('');
  const [extraResults, setExtraResults]             = useState<ExtraItem[]>([]);
  const [extraVendors, setExtraVendors]             = useState<string[]>([]);
  const [extraTypes, setExtraTypes]                 = useState<string[]>([]);
  const [extraSearching, setExtraSearching]         = useState(false);
  const [selectedExtras, setSelectedExtras]         = useState<SelectedExtra[]>([]);
  const [rows, setRows]           = useState<CountSheetRow[]>([]);

  // ── Monthly Process state — OS-INV-21 ────────────────────────────────
  const [cyclePhase, setCyclePhase]       = useState<CycleCheckPhase>('idle');
  const [activeCycle, setActiveCycle]     = useState<ActiveCycle | null>(null);
  const [weekSchedule, setWeekSchedule]   = useState<WeekSchedule | null>(null);
  const [monthlyResult, setMonthlyResult] = useState<MonthlySessionResult | null>(null);
  const [monthlyItems, setMonthlyItems]       = useState<SessionItem[] | null>(null);
  const [monthlyItemsLoading, setMonthlyItemsLoading] = useState(false);
  const [monthlyQrDataUrl, setMonthlyQrDataUrl] = useState<string>('');

  // ── Fetch items for monthly process success view ────────────────────
  useEffect(() => {
    if (monthlyResult?.session_id && phase === 'monthly-done') {
      setMonthlyItemsLoading(true);
      authFetch(`${apiBase}/count-sessions/${monthlyResult.session_id}/items`)
        .then(async (res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          setMonthlyItems(data.items ?? []);
        })
        .catch((e) => {
          console.error('Failed to fetch count sheet items:', e);
          setMonthlyItems([]);
        })
        .finally(() => setMonthlyItemsLoading(false));
    }
  }, [monthlyResult?.session_id, phase, apiBase]);

  // ── Auto-print when All-Items sheet is ready (unchanged) ─────────────
  useEffect(() => {
    if (phase === 'ready' && rows.length > 0 && qrDataUrl) {
      const t = setTimeout(() => window.print(), 500);
      return () => clearTimeout(t);
    }
  }, [phase, rows, qrDataUrl]);

  // #1: Generate per-item QR codes whenever rows or items change
  useEffect(() => {
    const origin = window.location.origin;
    const itemsToCode: Array<{ key: string; itemNumber: number | string; loc: string }> = [];

    // Monthly-process items
    if (monthlyItems) {
      for (const item of monthlyItems) {
        const loc = item.location ?? location;
        itemsToCode.push({ key: `${item.itemNumber}:${loc}`, itemNumber: item.itemNumber, loc });
      }
    }
    // All-items rows
    for (const r of rows) {
      itemsToCode.push({ key: `${r.itemNumber}:${r.location}`, itemNumber: r.itemNumber, loc: r.location });
    }

    if (itemsToCode.length === 0) return;
    const newQRs: Record<string, string> = {};
    Promise.all(
      itemsToCode.map(({ key, itemNumber, loc }) =>
        QRCode.toDataURL(
          `${origin}/os/scan/${itemNumber}/${loc}?src=sheet`,
          { width: 80, margin: 1 }
        ).then((url: string) => { newQRs[key] = url; })
      )
    ).then(() => setItemQRs(prev => ({ ...prev, ...newQRs })));
  }, [rows, monthlyItems, location]);

  // ── OS-INV-21: cycle check — fires when Monthly Process is selected ──
  useEffect(() => {
    if (mode !== 'monthly_process') {
      // Reset cycle state when user switches back to All Items.
      setCyclePhase('idle');
      return;
    }

    setCyclePhase('checking');
    setActiveCycle(null);
    setWeekSchedule(null);

    let cancelled = false;

    const check = async () => {
      try {
        // Step 1: is there an active cycle?
        const cycleRes = await authFetch(`${apiBase}/audit/cycles/active`);
        if (cancelled) return;

        if (cycleRes.status === 404) {
          setCyclePhase('none');
          return;
        }
        if (!cycleRes.ok) {
          setCyclePhase('check-error');
          return;
        }

        const cycle: ActiveCycle = await cycleRes.json();
        if (cancelled) return;
        setActiveCycle(cycle);

        // Step 2: fetch this week's schedule for the pre-gen summary.
        // Failure here is non-fatal — we still enable the generate button.
        try {
          const weekRes = await authFetch(`${apiBase}/audit/schedule/week`);
          if (!cancelled && weekRes.ok) {
            const week: WeekSchedule = await weekRes.json();
            setWeekSchedule(week);
          }
        } catch {
          // Week schedule is informational only; proceed without it.
        }

        if (!cancelled) setCyclePhase('active');
      } catch {
        if (!cancelled) setCyclePhase('check-error');
      }
    };

    check();
    return () => { cancelled = true; };
  }, [mode, apiBase]);

  // ── Handlers ──────────────────────────────────────────────────────────

  // MADE-299: fetch filtered extra items from the backend
  const searchExtras = async () => {
    setExtraSearching(true);
    try {
      const params = new URLSearchParams();
      if (extraVendor)     params.set('vendor',      extraVendor);
      if (extraType)       params.set('type',        extraType);
      if (extraQtyMin)     params.set('quantityMin', extraQtyMin);
      if (extraQtyMax)     params.set('quantityMax', extraQtyMax);
      if (extraItemSearch) params.set('itemNumbers', extraItemSearch.replace(/\s/g, ''));
      const res = await authFetch(`${apiBase}/materials/extras-search?${params}`);
      if (!res.ok) throw new Error('Search failed');
      const data = await res.json() as { items: ExtraItem[]; vendors: string[]; types: string[] };
      setExtraResults(data.items ?? []);
      setExtraVendors(data.vendors ?? []);
      setExtraTypes(data.types ?? []);
    } catch { setExtraResults([]); }
    finally { setExtraSearching(false); }
  };

  const toggleExtra = (item: ExtraItem) => {
    const key = `${item.materialId}:${item.itemNumber}`;
    setSelectedExtras(prev => {
      const exists = prev.some(e => `${e.materialId}:${e.itemNumber}` === key);
      if (exists) return prev.filter(e => `${e.materialId}:${e.itemNumber}` !== key);
      return [...prev, { materialId: item.materialId, materialName: item.materialName, itemNumber: item.itemNumber, location: item.location }];
    });
  };

  const selectAllFilteredExtras = () => {
    setSelectedExtras(prev => {
      const existing = new Set(prev.map(e => `${e.materialId}:${e.itemNumber}`));
      const toAdd = extraResults.filter(r => !existing.has(`${r.materialId}:${r.itemNumber}`))
        .map(r => ({ materialId: r.materialId, materialName: r.materialName, itemNumber: r.itemNumber, location: r.location }));
      return [...prev, ...toAdd];
    });
  };

  const handleCreate = async () => {
    setPhase('loading');
    setErrorMsg('');

    if (mode === 'monthly_process') {
      // ── Monthly Process path ──────────────────────────────────────────
      try {
        if (!activeCycle) throw new Error('No active inventory cycle found.');

        const res = await authFetch(`${apiBase}/count-sessions`, {
          method: 'POST',
          body: JSON.stringify({
            mode: 'monthly_process',
            cycle_id: activeCycle.id,
            location,
            // MADE-299: extra items added by the manager at creation time
            additionalItems: selectedExtras.map(e => ({
              materialId:   e.materialId,
              materialName: e.materialName,
              itemNumber:   e.itemNumber,
              location:     e.location,
            })),
          }),
        });

        if (!res.ok) {
          const d = await res.json().catch(() => ({})) as { error?: string };
          throw new Error(d.error ?? `HTTP ${res.status}`);
        }

        const result: MonthlySessionResult = await res.json();
        setMonthlyResult(result);

        // Generate QR code for the count entry URL.
        const countFullUrl = `${window.location.origin}${result.count_url ?? `${routeBase}/count/${result.session_id}`}`;
        try {
          const qr = await QRCode.toDataURL(countFullUrl, { width: 200, margin: 1 });
          setMonthlyQrDataUrl(qr);
        } catch {
          // Non-fatal — sheet still usable without QR.
        }

        setPhase('monthly-done');
      } catch (e: unknown) {
        setErrorMsg((e as Error).message ?? 'Failed to generate monthly process sheet');
        setPhase('error');
      }
      return;
    }

    // ── All Items path (existing — unchanged) ─────────────────────────
    try {
      const sessionRes = await authFetch(`${apiBase}/count-sessions`, {
        method: 'POST',
        body: JSON.stringify({
          createdBy: 'inventory-manager',
          location,
        }),
      });
      if (!sessionRes.ok) {
        const d = await sessionRes.json().catch(() => ({})) as { error?: string };
        throw new Error(d.error ?? `HTTP ${sessionRes.status}`);
      }

      const sessionData = await sessionRes.json() as { session: CountSession & { location?: CountLocation } };
      const sess: CountSession = {
        ...sessionData.session,
        location: sessionData.session.location ?? location,
      };
      setSession(sess);

      const countUrl = `${window.location.origin}${routeBase}/count/${sess.sessionId}`;
      const qr = await QRCode.toDataURL(countUrl, { width: 200, margin: 1 });
      setQrDataUrl(qr);

      const matRes = await authFetch(`${apiBase}/materials`);
      if (!matRes.ok) throw new Error(`Materials fetch failed: HTTP ${matRes.status}`);
      const matData = await matRes.json() as { materials?: OsMaterial[] };
      const mats: OsMaterial[] = matData.materials ?? [];

      setRows(buildRows(mats, sess.location ?? location));
      setPhase('ready');
    } catch (e: unknown) {
      setErrorMsg((e as Error).message ?? 'Failed to create count session');
      setPhase('error');
    }
  };

  const handleReset = () => {
    setPhase('setup');
    setSession(null);
    setQrDataUrl('');
    setRows([]);
    setErrorMsg('');
    setMonthlyResult(null);
    setMonthlyQrDataUrl('');
    // Retain mode + cycle state — user may want another sheet in the same mode.
    // The cycle re-check runs automatically if they switch mode.
  };

  // ── Derived values ─────────────────────────────────────────────────────

  // Overdue count for the pre-gen summary: prefer week-level detail;
  // fall back to cycle-level progress.overdue.
  const weekOverdueCount: number = weekSchedule
    ? weekSchedule.items.filter(i => i.is_overdue === true || i.urgency === 'overdue').length
    : (activeCycle?.progress.overdue ?? 0);

  // Disable the generate button in Monthly Process mode unless cycle is confirmed.
  const generateDisabled =
    mode === 'monthly_process' && cyclePhase !== 'active';

  // ── Render: SETUP ──────────────────────────────────────────────────────
  if (phase === 'setup') {
    return (
      <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 520, mx: 'auto', mt: 4 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          New Count Session
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose a mode and location, then generate a count sheet for warehouse staff.
        </Typography>

        {/* ── OS-INV-21: Mode selector ────────────────────────────────── */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Count Mode
          </Typography>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(_e, v: GeneratorMode | null) => { if (v) setMode(v); }}
            fullWidth
            size="small"
          >
            <ToggleButton value="monthly_process" sx={{ textTransform: 'none', fontWeight: 600 }}>
              Inventory Cycle
            </ToggleButton>
            <ToggleButton value="all" sx={{ textTransform: 'none', fontWeight: 600 }}>
              All Items
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* ── All Items: brief description ─────────────────────────────── */}
        {mode === 'all' && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Generates a full count sheet with every item in the catalog for the
            chosen location. Use this for periodic full inventory walks.
          </Typography>
        )}

        {/* ── OS-INV-21: Monthly Process panel ────────────────────────── */}
        {mode === 'monthly_process' && (
          <Box sx={{ mb: 3 }}>
            {/* Checking */}
            {cyclePhase === 'checking' && (
              <Stack
                direction="row" spacing={1.5} alignItems="center"
                sx={{ p: 2, bgcolor: '#f5f8ff', borderRadius: 2, border: '1px solid #e0e7ff' }}
              >
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Checking for active inventory cycle…
                </Typography>
              </Stack>
            )}

            {/* No active cycle — AC 8 */}
            {cyclePhase === 'none' && (
              <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ borderRadius: 2 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  No active inventory cycle.
                </Typography>
                <Typography variant="body2">
                  Start a monthly inventory cycle from the{' '}
                  <RouterLink
                    to={`${routeBase}/audit`}
                    style={{ color: 'inherit', fontWeight: 700 }}
                  >
                    Inventory Management dashboard
                  </RouterLink>
                  {' '}first.
                </Typography>
              </Alert>
            )}

            {/* Cycle service unreachable */}
            {cyclePhase === 'check-error' && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                Could not reach the cycle scheduling service. Check your connection and try again.
              </Alert>
            )}

            {/* Active cycle — pre-gen summary — AC 9 */}
            {cyclePhase === 'active' && activeCycle && (
              <Paper
                variant="outlined"
                sx={{ p: 2, borderRadius: 2, bgcolor: '#f5f8ff', borderColor: '#c7d7f5' }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <InfoOutlinedIcon
                    fontSize="small"
                    sx={{ color: '#1565c0', mt: 0.25, flexShrink: 0 }}
                  />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} color="#1565c0">
                      {activeCycle.name}
                      {weekSchedule && ` — Week ${weekSchedule.week}`}
                    </Typography>

                    {weekSchedule ? (
                      <Typography variant="body2" color="text.secondary">
                        {weekSchedule.items.length}{' '}
                        item{weekSchedule.items.length !== 1 ? 's' : ''} due this week
                        {weekOverdueCount > 0 && (
                          <Box component="span" sx={{ color: '#c62828', fontWeight: 700 }}>
                            {' '}({weekOverdueCount} overdue)
                          </Box>
                        )}
                        {weekSchedule.total_remaining_this_cycle != null && (
                          <Box component="span" color="text.disabled">
                            {' '}· {weekSchedule.total_remaining_this_cycle} remaining this cycle
                          </Box>
                        )}
                      </Typography>
                    ) : (
                      // Week schedule unavailable — still enable generate
                      <Typography variant="body2" color="text.secondary">
                        {activeCycle.progress.overdue > 0
                          ? `${activeCycle.progress.overdue} overdue items will be prioritised.`
                          : "Sheet will include this week's due items."}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Paper>
            )}
          </Box>
        )}

        {/* ── Location picker (both modes) ─────────────────────────────── */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Location to Count</InputLabel>
          <Select
            value={location}
            label="Location to Count"
            onChange={e => setLocation(e.target.value as CountLocation)}
          >
            <MenuItem value="all">All Locations (entire inventory)</MenuItem>
            <MenuItem value="storeroom">Store Room only</MenuItem>
            <MenuItem value="working">Working Inventory only</MenuItem>
          </Select>
        </FormControl>

        {/* MADE-299: extra items panel — monthly_process only */}
        {mode === 'monthly_process' && (
          <Box sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box
              sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', bgcolor: 'grey.50', borderRadius: extraPanelOpen ? '8px 8px 0 0' : 2 }}
              onClick={() => { setExtraPanelOpen(p => !p); if (!extraPanelOpen && extraVendors.length === 0) searchExtras(); }}
              data-testid="extra-items-toggle"
            >
              <Typography variant="body2" fontWeight={600}>
                Add Extra Items {selectedExtras.length > 0 && `(${selectedExtras.length} selected)`}
              </Typography>
              <Typography variant="caption" color="text.secondary">{extraPanelOpen ? '▲' : '▼'}</Typography>
            </Box>
            {extraPanelOpen && (
              <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Stack spacing={1.5} mb={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Vendor</InputLabel>
                      <Select value={extraVendor} label="Vendor" onChange={e => setExtraVendor(e.target.value as string)}>
                        <MenuItem value="">Any</MenuItem>
                        {extraVendors.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Type</InputLabel>
                      <Select value={extraType} label="Type" onChange={e => setExtraType(e.target.value as string)}>
                        <MenuItem value="">Any</MenuItem>
                        {extraTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField size="small" label="Min Qty" type="number" value={extraQtyMin} onChange={e => setExtraQtyMin(e.target.value)} sx={{ flex: 1 }} />
                    <TextField size="small" label="Max Qty" type="number" value={extraQtyMax} onChange={e => setExtraQtyMax(e.target.value)} sx={{ flex: 1 }} />
                    <TextField size="small" label="Item # (comma-separated)" value={extraItemSearch} onChange={e => setExtraItemSearch(e.target.value)} sx={{ flex: 2 }} />
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="contained" onClick={searchExtras} disabled={extraSearching} data-testid="search-extras-btn">
                      {extraSearching ? 'Searching…' : 'Filter'}
                    </Button>
                    {extraResults.length > 0 && (
                      <Button size="small" variant="outlined" onClick={selectAllFilteredExtras} data-testid="select-all-extras-btn">
                        Select All ({extraResults.length})
                      </Button>
                    )}
                  </Stack>
                </Stack>
                {extraResults.length > 0 && (
                  <Box sx={{ maxHeight: 240, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1.5 }}>
                    {extraResults.map(item => {
                      const key = `${item.materialId}:${item.itemNumber}`;
                      const selected = selectedExtras.some(e => `${e.materialId}:${e.itemNumber}` === key);
                      return (
                        <Box key={key} sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
                          <Checkbox size="small" checked={selected} onChange={() => toggleExtra(item)} data-testid={`extra-item-${item.itemNumber}`} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={selected ? 700 : 400}>{item.materialName}</Typography>
                            <Typography variant="caption" color="text.secondary">{String(item.itemNumber).padStart(6,'0')} · {item.location} · Qty: {item.currentStock}</Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
                {selectedExtras.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {selectedExtras.map(e => (
                      <Chip key={`${e.materialId}:${e.itemNumber}`} label={`${String(e.itemNumber).padStart(6,'0')} — ${e.materialName.slice(0,20)}`} size="small" onDelete={() => toggleExtra({ ...e, type: '', vendor: null, currentStock: 0 } as ExtraItem)} />
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleCreate}
          disabled={generateDisabled}
          sx={{
            bgcolor: '#001550',
            '&:hover': { bgcolor: '#002680' },
            '&:disabled': { bgcolor: '#90a4ae', color: '#fff' },
          }}
        >
          {mode === 'monthly_process' ? (selectedExtras.length > 0 ? `Generate Sheet (+${selectedExtras.length} extra items)` : "Generate This Week's Sheet") : 'Create Count Sheet'}
        </Button>
      </Box>
    );
  }

  // ── Render: LOADING ────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        minHeight: '60vh', gap: 2,
      }}>
        <CircularProgress />
        <Typography color="text.secondary">
          {mode === 'monthly_process' ? 'Building inventory cycle sheet…' : 'Creating session…'}
        </Typography>
      </Box>
    );
  }

  // ── Render: ERROR ──────────────────────────────────────────────────────
  if (phase === 'error') {
    return (
      <Box sx={{ p: 4, maxWidth: 480, mx: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>
        <Button variant="outlined" onClick={handleReset}>Try Again</Button>
      </Box>
    );
  }

  // ── Render: MONTHLY-DONE ── OS-INV-21 AC 10 ───────────────────────────
  // Shows a printable count sheet for the generated monthly-process session.
  // Action buttons are screen-only; the table prints cleanly.
  if (phase === 'monthly-done' && monthlyResult) {
    const { item_count, overdue_included, class_breakdown, count_url, week, cycle_id, session_id } = monthlyResult;
    const cycleName = activeCycle?.name ?? `Cycle ${cycle_id}`;
    const locMeta = LOCATION_META[location];

    return (
      <Box sx={{ p: { xs: 2, sm: 4 }, '@media print': { p: 2 } }}>

        {/* ── Screen-only: action bar + summary ─────────────────────── */}
        <Box sx={{ '@media print': { display: 'none' }, mb: 3 }}>
          {/* Action buttons */}
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mb: 3 }}>
            {/* #4: copies stepper — also on monthly-done view */}
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Button size="small" variant="outlined" onClick={() => setNCopies(c => Math.max(1, c - 1))} sx={{ minWidth: 32, px: 0.5 }}>−</Button>
              <Typography variant="body2" sx={{ minWidth: 90, textAlign: 'center' }}>
                {nCopies === 1 ? '1 copy (100%)' : `${nCopies} copies (~${Math.round(120/nCopies)}%)`}
              </Typography>
              <Button size="small" variant="outlined" onClick={() => setNCopies(c => Math.min(8, c + 1))} sx={{ minWidth: 32, px: 0.5 }}>+</Button>
            </Stack>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
              sx={{ bgcolor: '#001550', '&:hover': { bgcolor: '#002680' } }}
            >
              {nCopies > 1 ? `Print ${nCopies} Copies` : 'Print This Sheet'}
            </Button>
            <Button
              variant="outlined"
              component="a"
              href={count_url ?? `/os/count/${session_id}`}
            >
              Open Entry Form
            </Button>
            <Button variant="outlined" onClick={handleReset} color="inherit">
              Generate Another
            </Button>
          </Stack>
          {nCopies > 1 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Each copy contains ~{Math.round(120/nCopies)}% of items — items appear on multiple copies for double-counting.
            </Typography>
          )}

          {/* Generated summary card */}
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: '#f0fdf4', borderColor: '#a7f3d0' }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
              <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 22 }} />
              <Typography variant="subtitle1" fontWeight={700}>
                Sheet Generated — {cycleName}, Week {week}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={3} flexWrap="wrap">
              <Typography variant="body2" color="text.secondary">
                <strong>{item_count ?? 0}</strong> items
              </Typography>
              {(overdue_included ?? 0) > 0 && (
                <Typography variant="body2" color="#c62828">
                  <strong>{overdue_included}</strong> overdue
                </Typography>
              )}
              <Stack direction="row" spacing={0.75}>
                {(['A', 'B', 'C'] as const).map(cls => (
                  <Chip
                    key={cls}
                    label={`${cls}: ${(class_breakdown ?? {})[cls] ?? 0}`}
                    size="small"
                    sx={{ fontWeight: 700, fontSize: '0.7rem', ...CLASS_CHIP_SX[cls] }}
                  />
                ))}
              </Stack>
            </Stack>
          </Paper>
        </Box>

        {/* ── Printable sheet header ────────────────────────────────── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
              Inventory Count Sheet
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 0.5 }}>
              {cycleName} — Week {week}
            </Typography>
            <Chip
              label={`Counting: ${locMeta.label}`}
              color={locMeta.color}
              sx={{ fontWeight: 700, fontSize: '0.9rem', height: 32 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Session: {session_id.substring(0, 8)}… • Generated: {new Date().toLocaleDateString()}
            </Typography>
          </Box>
          {/* QR code */}
          <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
            {monthlyQrDataUrl && (
              <>
                <img src={monthlyQrDataUrl} alt="Count Session QR Code" style={{ width: 150, height: 150 }} />
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Scan to count
                </Typography>
              </>
            )}
          </Box>
        </Box>

        {/* ── Instructions ─────────────────────────────────────────── */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5', '@media print': { border: '1px solid #ccc' } }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>Instructions:</Typography>
          <Typography variant="body2" component="ol" sx={{ pl: 2, m: 0 }}>
            <li>Scan the QR code with the inventory scanner</li>
            <li>Count the physical items in the location listed</li>
            <li>Enter count on the inventory scanner</li>
            <li>Report any problems to your supervisor</li>
          </Typography>
        </Paper>

        {/* ── Count sheet table ─────────────────────────────────────── */}
        {monthlyItemsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!monthlyItemsLoading && monthlyItems !== null && monthlyItems.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No items were scheduled for this week. This may mean all items are up to date.
          </Alert>
        )}

        {!monthlyItemsLoading && monthlyItems && monthlyItems.length > 0 && (
          <Table sx={{ '@media print': { fontSize: '10pt' } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#1976d2' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 700, width: 120 }}>Item #</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Material Name</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, width: 140 }}>Location</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, width: 80, textAlign: 'center' }}>Unit</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 700, width: 150, borderLeft: '2px solid #fff' }}>Physical Count</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {monthlyItems.map((item, i) => (
                <TableRow
                  key={`${item.materialId}::${item.itemNumber}`}
                  sx={{
                    bgcolor: i % 2 === 0 ? 'white' : '#f9f9f9',
                    '@media print': { pageBreakInside: 'avoid', borderBottom: '1px solid #ddd' },
                  }}
                >
                  <TableCell sx={{ fontWeight: 700, fontSize: '14pt' }}>
                    {String(item.itemNumber).padStart(6, '0')}
                  </TableCell>
                  <TableCell>{item.materialName}</TableCell>
                  <TableCell>
                    {item.location ? (
                      <Chip
                        label={ROW_LOCATION_LABEL[item.location]}
                        size="small"
                        color={item.location === 'storeroom' ? 'success' : 'primary'}
                        sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                      />
                    ) : '—'}
                  </TableCell>
                  <TableCell align="center">{item.unit ?? '—'}</TableCell>
                  <TableCell sx={{ borderLeft: '2px solid #ddd', textAlign: 'center' }}>
                    {itemQRs[`${item.itemNumber}:${item.location ?? location}`]
                      ? <img src={itemQRs[`${item.itemNumber}:${item.location ?? location}`]} alt={`QR ${item.itemNumber}`} width={64} height={64} style={{ display: 'block', margin: '0 auto' }} />
                      : <span style={{ fontSize: '0.7rem', color: '#999' }}>…</span>
                    }
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #ddd', '@media print': { mt: 2 } }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Offshore Sails Inventory • {item_count ?? 0} items • {cycleName} Week {week} • {locMeta.label}
          </Typography>
        </Box>
      </Box>
    );
  }


  // ── Render: READY (All Items — printable table, unchanged) ────────────
  const locMeta = LOCATION_META[session?.location ?? location];
  const countLabel = `Counting: ${locMeta.label}`;

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, '@media print': { p: 2 } }}>
      {/* ── Print controls (screen only) ──────────────────────────────── */}
      <Box className="no-print" sx={{ mb: 3, '@media print': { display: 'none' } }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" gap={1}>
          {/* #4: copies stepper */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Button size="small" variant="outlined" onClick={() => setNCopies(c => Math.max(1, c - 1))} sx={{ minWidth: 32, px: 0.5 }}>−</Button>
            <Typography variant="body2" sx={{ minWidth: 80, textAlign: 'center' }}>
              {nCopies} {nCopies === 1 ? 'copy (100%)' : `copies (~${Math.round(120/nCopies)}% each)`}
            </Typography>
            <Button size="small" variant="outlined" onClick={() => setNCopies(c => Math.min(8, c + 1))} sx={{ minWidth: 32, px: 0.5 }}>+</Button>
          </Stack>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
            sx={{ bgcolor: '#001550', '&:hover': { bgcolor: '#002680' } }}
          >
            Print {nCopies > 1 ? `${nCopies} Copies` : 'Sheet'}
          </Button>
          <Button variant="outlined" onClick={handleReset} color="inherit">
            New Session
          </Button>
        </Stack>
        {nCopies > 1 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1, mb: 1 }}>
            Each copy contains ~{Math.round(120/nCopies)}% of items — items appear on multiple copies for double-counting.
          </Typography>
        )}
      </Box>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
            Inventory Count Sheet
          </Typography>

          {/* OS-INV-16: prominent location label */}
          <Chip
            label={countLabel}
            color={locMeta.color}
            sx={{ mb: 1, fontWeight: 700, fontSize: '0.9rem', height: 32 }}
          />

          <Typography variant="body2" color="text.secondary">
            Session: {session?.sessionId.substring(0, 8)}…
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Created: {session && new Date(session.createdAt).toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Expires: {session && new Date(session.expiresAt).toLocaleString()}
          </Typography>
        </Box>

        {/* QR code */}
        <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
          {qrDataUrl && (
            <>
              <img src={qrDataUrl} alt="Count Session QR Code" style={{ width: 150, height: 150 }} />
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                Scan to count
              </Typography>
            </>
          )}
        </Box>
      </Box>

      {/* ── Instructions ─────────────────────────────────────────────────── */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5', '@media print': { border: '1px solid #ccc' } }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>Instructions:</Typography>
        <Typography variant="body2" component="ol" sx={{ pl: 2, m: 0 }}>
          <li>Scan the QR code with the inventory scanner</li>
          <li>Count the physical items in the location listed</li>
          <li>Enter count on the inventory scanner</li>
          <li>Report any problems to your supervisor</li>
        </Typography>
      </Paper>

      {/* ── Materials table ───────────────────────────────────────────────── */}
      {rows.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No items with system-assigned item numbers found for this location. Item
          numbers are assigned automatically when a location holds stock — open the
          inventory list once to trigger assignment, or add stock to a location.
        </Alert>
      )}

      <Table sx={{ '@media print': { fontSize: '10pt' } }}>
        <TableHead>
          <TableRow sx={{ bgcolor: '#1976d2' }}>
            <TableCell sx={{ color: '#fff', fontWeight: 700, width: 120 }}>Item #</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Material Name</TableCell>
            {/* OS-INV-17: each row is location-specific; show which one */}
            <TableCell sx={{ color: '#fff', fontWeight: 700, width: 120 }}>Location</TableCell>
            {/* OS-INV-18: System Count column removed — blind count.
                Employees receiving the printed sheet must not see system quantities. */}
            <TableCell sx={{ color: '#fff', fontWeight: 700, width: 80, textAlign: 'center' }}>Unit</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 700, width: 150, borderLeft: '2px solid #fff' }}>
              Physical Count
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow
              key={r.key}
              sx={{
                bgcolor: i % 2 === 0 ? 'white' : '#f9f9f9',
                '@media print': { pageBreakInside: 'avoid', borderBottom: '1px solid #ddd' },
              }}
            >
              <TableCell sx={{ fontWeight: 700, fontSize: '14pt' }}>
                {r.itemNumber.toString().padStart(6, '0')}
              </TableCell>
              <TableCell>{r.name}</TableCell>
              <TableCell>
                <Chip
                  label={ROW_LOCATION_LABEL[r.location]}
                  size="small"
                  color={r.location === 'storeroom' ? 'success' : 'primary'}
                  sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                />
              </TableCell>
              {/* OS-INV-18: systemCount intentionally not rendered — blind count. */}
              <TableCell align="center">{r.unit}</TableCell>
              <TableCell sx={{ borderLeft: '2px solid #ddd', textAlign: 'center' }}>
                {itemQRs[`${r.itemNumber}:${r.location}`]
                  ? <img src={itemQRs[`${r.itemNumber}:${r.location}`]} alt={`QR ${r.itemNumber}`} width={64} height={64} style={{ display: 'block', margin: '0 auto' }} />
                  : <span style={{ fontSize: '0.7rem', color: '#999' }}>…</span>
                }
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Footer for copy 1 */}
      <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #ddd', '@media print': { mt: 2 } }}>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Offshore Sails Inventory • {rows.length} items • {countLabel}
          {nCopies > 1 ? ` • Copy 1 of ${nCopies}` : ''}
        </Typography>
      </Box>

      {/* #4: Additional copies — same table structure, different item subsets */}
      {nCopies > 1 && buildCopies(rows, nCopies).slice(1).map((copyRows, idx) => {
        const copyNum = idx + 2;
        return (
          <Box key={copyNum} sx={{ mt: 6, pageBreakBefore: 'always' }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#001550' }}>
              Copy {copyNum} of {nCopies} — {copyRows.length} items (~{Math.round(120/nCopies)}% of total)
            </Typography>
            <Table size="small" sx={{ tableLayout: 'fixed', '@media print': { fontSize: '10pt' } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#001550' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, width: 90 }}>Item No.</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Description</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, width: 120 }}>Location</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, width: 80, textAlign: 'center' }}>Unit</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, width: 90, borderLeft: '2px solid #fff', textAlign: 'center' }}>Scan</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {copyRows.map((r, i) => (
                  <TableRow key={r.key} sx={{ bgcolor: i % 2 === 0 ? 'white' : '#f9f9f9', '@media print': { pageBreakInside: 'avoid', borderBottom: '1px solid #ddd' } }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14pt' }}>{r.itemNumber.toString().padStart(6, '0')}</TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell><Chip label={ROW_LOCATION_LABEL[r.location]} size="small" color={r.location === 'storeroom' ? 'success' : 'primary'} sx={{ fontWeight: 700, fontSize: '0.7rem' }} /></TableCell>
                    <TableCell align="center">{r.unit}</TableCell>
                    <TableCell sx={{ borderLeft: '2px solid #ddd', textAlign: 'center' }}>
                      {itemQRs[`${r.itemNumber}:${r.location}`]
                        ? <img src={itemQRs[`${r.itemNumber}:${r.location}`]} alt={`QR ${r.itemNumber}`} width={64} height={64} style={{ display: 'block', margin: '0 auto' }} />
                        : <span style={{ fontSize: '0.7rem', color: '#999' }}>…</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid #ddd' }}>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Offshore Sails Inventory • {copyRows.length} items • {countLabel} • Copy {copyNum} of {nCopies}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default OsInventoryCountSheet;
